import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  X, FileText, Edit2, Save, ArrowRight, AlertCircle, XCircle, Pause,
} from 'lucide-react';
import StyleStep from '../customization/StyleStep';
import PreferencesStep from '../customization/PreferencesStep';
import { API, STATUS_CONFIG, TRANSITIONS, Section } from './utils';

const isClosingWithVisible = (fieldKey, prefs) => {
  const prefix = fieldKey.replace(/closing_with$/, '').replace(/_$/, '');
  const sideKey = prefix ? `${prefix}_closing_side` : 'closing_side';
  return prefs[sideKey] && prefs[sideKey] !== 'na';
};

const isoDateInputFormat = (v) => {
  if (!v) return '';
  try { return new Date(v).toISOString().slice(0,10); } catch { return ''; }
};

const filterSareeSchema = (fields, order, catSlug) => {
  if (catSlug !== 'ready-to-wear-saree') return fields;
  const withBlouse = order?.preferences?.with_blouse?.value === 'yes';
  if (withBlouse) return fields;
  return (fields || []).filter(f => f.section !== 'Blouse');
};

export default function OrderPopup({ order: initialOrder, onClose, onRefresh, role }) {
  const [order, setOrder] = useState(initialOrder);
  const [category, setCategory] = useState(null);
  const [catLoading, setCatLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editStyle, setEditStyle] = useState({});
  const [editPrefs, setEditPrefs] = useState({});
  const [editMeasurement, setEditMeasurement] = useState({});
  const [editStyleActiveSection, setEditStyleActiveSection] = useState(null);
  const [estCost, setEstCost] = useState('');
  const [estDelivery, setEstDelivery] = useState(isoDateInputFormat(initialOrder.estimated_delivery));
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Delivered dialog state
  const [deliveredOpen, setDeliveredOpen] = useState(false);
  const [deliveredAmountPaid, setDeliveredAmountPaid] = useState(String(initialOrder.amount_paid || ''));
  const [deliveredComment, setDeliveredComment] = useState('');
  // Record-payment dialog state (for incremental payments on a partial-paid order)
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');

  const isTailor = role === 'TAILOR';
  const canEdit = !isTailor && ['NEW_ORDER','VERIFICATION_VISIT','ON_HOLD'].includes(order.status);
  const allowed = TRANSITIONS[order.status] || [];
  const isMoveBack = (target) => target === 'APPROVED' && ['READY_FOR_DELIVERY','DELIVERED'].includes(order.status);
  const canMoveBack = ['SUPER_ADMIN','BRANCH_ADMIN'].includes(role);

  useEffect(() => {
    if (!order.category_name) { setCatLoading(false); return; }
    api.get('/categories').then(r => {
      const cats = r.data.data || [];
      setCategory(cats.find(c => c.name === order.category_name) || null);
    }).catch(()=>{}).finally(() => setCatLoading(false));
  }, [order.category_name]);

  const startEdit = () => {
    setEditStyle(order.style || {});
    setEditPrefs(order.preferences || {});
    setEditMeasurement(order.measurement || {});
    setEditStyleActiveSection(category?.design_fields?.[0]?.field_key || null);
    setEditing(true);
  };

  const saveEdit = async () => {
    setSubmitting(true);
    try {
      await api.put(`/admin/orders/${order.id}/details`, { style: editStyle, preferences: editPrefs, measurement: editMeasurement });
      try {
        const r = await api.get(`/admin/orders/${order.id}`);
        if (r.data?.data) setOrder(r.data.data);
      } catch {
        // best-effort
      }
      toast.success('Order updated');
      setEditing(false);
      onRefresh();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
    finally { setSubmitting(false); }
  };

  const collectMissing = (forStatus) => {
    if (!category) return ['Category data still loading — please wait'];
    const warnings = [];
    const style = editing ? editStyle : (order.style || {});
    const prefs = editing ? editPrefs : (order.preferences || {});
    const meas = editing ? editMeasurement : (order.measurement || {});

    if (forStatus === 'PENDING_APPROVAL') {
      const orderForFilter = { preferences: prefs };
      filterSareeSchema(category.design_fields, orderForFilter, category.slug).forEach(df => {
        if (!style[df.field_key] || !style[df.field_key].key) warnings.push(`Design: ${df.field_label}`);
      });
      filterSareeSchema(category.preferences_schema, orderForFilter, category.slug).filter(p => p.required).forEach(p => {
        if ((p.key.endsWith('closing_with') || p.key === 'closing_with') && !isClosingWithVisible(p.key, prefs)) return;
        const v = prefs[p.key];
        const isEmpty = v === undefined || v === null || v === '' ||
          (Array.isArray(v) && v.length === 0) ||
          (typeof v === 'object' && !Array.isArray(v) && !v.value && (!v.values || v.values.length === 0));
        if (isEmpty) warnings.push(`Preference: ${p.label}`);
      });
      (category.measurement_fields || []).filter(m => m.is_required).forEach(m => {
        const v = meas[m.field_key];
        if (v === undefined || v === null || v === '') warnings.push(`Measurement: ${m.field_label}`);
      });
    }
    return warnings;
  };

  const submitDelivered = async () => {
    setSubmitting(true);
    try {
      const paid = parseFloat(deliveredAmountPaid);
      await api.put(`/admin/orders/${order.id}/status`, {
        status: 'DELIVERED',
        notes,
        amount_paid: isNaN(paid) ? 0 : paid,
        delivery_comment: deliveredComment,
      });
      toast.success('Marked as Delivered');
      setDeliveredOpen(false);
      onClose();
      onRefresh();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
    finally { setSubmitting(false); }
  };

  const submitPayment = async () => {
    const delta = parseFloat(paymentAmount);
    if (isNaN(delta) || delta <= 0) { toast.error('Enter a valid payment amount'); return; }
    const currentPaid = parseFloat(order.amount_paid) || 0;
    const total = parseFloat(order.total_amount) || 0;
    const newPaid = Math.min(total, currentPaid + delta);
    setSubmitting(true);
    try {
      await api.put(`/admin/orders/${order.id}/details`, { amount_paid: newPaid });
      toast.success(newPaid >= total ? 'Fully paid — order moved out of Partial Paid' : `Payment recorded • Balance Rs. ${total - newPaid}`);
      setOrder(o => ({ ...o, amount_paid: newPaid }));
      setPaymentOpen(false);
      setPaymentAmount('');
      onRefresh();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
    finally { setSubmitting(false); }
  };

  const markFullyPaid = async () => {
    const total = parseFloat(order.total_amount) || 0;
    if (total <= 0) { toast.error('Order has no total amount set'); return; }
    setSubmitting(true);
    try {
      await api.put(`/admin/orders/${order.id}/details`, { amount_paid: total });
      toast.success('Marked as fully paid');
      setOrder(o => ({ ...o, amount_paid: total }));
      onRefresh();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleTransition = async (status) => {
    // V&V step: require estimated cost + estimated delivery date
    if (order.status === 'NEW_ORDER' && status === 'VERIFICATION_VISIT') {
      const hasExisting = order.total_amount && order.total_amount > 0;
      const cost = parseFloat(estCost);
      if (!hasExisting && (!estCost || isNaN(cost) || cost <= 0)) {
        toast.error('Estimated cost (Rs.) is required before moving to Verification & Visit.');
        return;
      }
      if (!estDelivery) {
        toast.error('Estimated delivery date is required before moving to Verification & Visit.');
        return;
      }
    }

    // Move back gate — only Super Admin + Branch Admin
    if (isMoveBack(status) && !canMoveBack) {
      toast.error('Only Super Admin or Branch Admin can move orders back for alteration.');
      return;
    }

    // Mark Delivered flow → open the delivered dialog instead of firing directly
    if (status === 'DELIVERED') {
      setDeliveredOpen(true);
      return;
    }

    // Validate required fields before PENDING_APPROVAL
    if (status === 'PENDING_APPROVAL') {
      if (catLoading) { toast.error('Category data loading. Please wait a moment.'); return; }
      if (!category) { toast.error('Category metadata unavailable — cannot validate completeness.'); return; }
      const warnings = collectMissing('PENDING_APPROVAL');
      if (warnings.length > 0) {
        toast.error(`Cannot move — missing required fields:\n• ${warnings.slice(0,8).join('\n• ')}${warnings.length>8?`\n…and ${warnings.length-8} more`:''}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      if (order.status === 'NEW_ORDER' && status === 'VERIFICATION_VISIT') {
        const payload = {};
        if (estCost) payload.total_amount = parseFloat(estCost);
        if (estDelivery) payload.estimated_delivery = estDelivery;
        if (Object.keys(payload).length > 0) {
          await api.put(`/admin/orders/${order.id}/details`, payload);
        }
      }
      await api.put(`/admin/orders/${order.id}/status`, { status, notes });
      toast.success(`Moved to ${STATUS_CONFIG[status]?.label||status}`);
      onClose();
      onRefresh();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
    finally { setSubmitting(false); }
  };

  const balance = (() => {
    const paid = parseFloat(deliveredAmountPaid);
    const total = parseFloat(order.total_amount) || 0;
    if (isNaN(paid)) return total;
    return Math.max(0, total - paid);
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="order-popup">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-bold">{order.order_number}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${STATUS_CONFIG[order.status]?.color||'bg-muted'}`}>{STATUS_CONFIG[order.status]?.label||order.status}</span>
              {order.sub_status === 'ALTERATION' && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600">Alteration</span>}
              {order.cancellation_requested && <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600">Cancel Req</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-muted" data-testid="popup-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Customer Info — Tailor hides phone + estimated price */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <Field label="Customer" value={order.customer_name || '-'} />
            {!isTailor && <Field label="Phone" value={order.customer_phone || '-'} />}
            <Field label="Category" value={order.category_name || '-'} />
            {!isTailor && <Field label="Estimated Amount" value={order.total_amount ? `Rs. ${order.total_amount}` : 'TBD'} />}
            {!isTailor && order.amount_paid > 0 && (
              <Field label="Amount Paid" value={`Rs. ${order.amount_paid}`} />
            )}
            {!isTailor && order.total_amount > 0 && order.amount_paid > 0 && order.amount_paid < order.total_amount && (
              <Field label="Balance Due" value={`Rs. ${Math.max(0, order.total_amount - order.amount_paid)}`} />
            )}
            <Field label="Priority" value={order.priority || 'Normal'} />
            <Field label="Created" value={new Date(order.created_at).toLocaleDateString()} />
            {order.estimated_delivery && (
              <Field label="Estimated Delivery" value={new Date(order.estimated_delivery).toLocaleDateString()} />
            )}
            {order.delivered_at && (
              <Field label="Delivered On" value={new Date(order.delivered_at).toLocaleDateString()} />
            )}
          </div>

          {/* Payment actions — visible to non-tailors when a balance is due */}
          {!isTailor && order.total_amount > 0 && (order.amount_paid || 0) < order.total_amount && ['SUPER_ADMIN','BRANCH_ADMIN','SALES_AGENT','PICKUP_AGENT'].includes(role) && (
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/40" data-testid="payment-actions">
              <Button size="sm" variant="outline" onClick={() => setPaymentOpen(true)} className="text-xs h-8" data-testid="record-payment-btn">
                Record Payment
              </Button>
              <Button size="sm" variant="outline" onClick={markFullyPaid} disabled={submitting} className="text-xs h-8 text-green-700 border-green-300 hover:bg-green-50" data-testid="mark-fully-paid-btn">
                Mark Fully Paid
              </Button>
              <span className="text-[11px] text-muted-foreground ml-auto">
                Paid Rs. {order.amount_paid || 0} of Rs. {order.total_amount} • Balance Rs. {Math.max(0, order.total_amount - (order.amount_paid || 0))}
              </span>
            </div>
          )}

          {!isTailor && (
            <p className="text-[11px] text-muted-foreground italic" data-testid="popup-estimated-disclaimer">
              * Estimated amount only. Final amount may vary after measurements, fabric and final tailoring.
            </p>
          )}

          {order.notes && <p className="text-xs bg-muted/50 rounded-lg p-3">{order.notes}</p>}
          {order.hold_reason && <p className="text-xs bg-amber-50 text-amber-700 rounded-lg p-3">Hold: {order.hold_reason}</p>}
          {order.delivery_comment && (
            <p className="text-xs bg-green-50 text-green-700 rounded-lg p-3" data-testid="popup-delivery-comment">
              Delivery note: {order.delivery_comment}
            </p>
          )}
          {order.pdf_url && <a href={`${API}${order.pdf_url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-primary font-medium hover:underline" data-testid="popup-pdf-link"><FileText className="w-3.5 h-3.5" /> Download PDF</a>}

          {/* Customer Address — Tailor hides address */}
          {!editing && !isTailor && order.address && (
            <Section title="Customer Address">
              <div className="text-xs bg-muted/30 rounded-lg p-3 leading-relaxed" data-testid="popup-address">
                {order.address.name && <p className="font-medium text-sm">{order.address.name}{order.address.phone && <span className="text-muted-foreground font-normal ml-2">{order.address.phone}</span>}</p>}
                <p>{[order.address.line1, order.address.line2].filter(Boolean).join(', ')}</p>
                <p>{[order.address.city, order.address.state].filter(Boolean).join(', ')} {order.address.pincode}</p>
                {order.address.landmark && <p className="text-muted-foreground">Landmark: {order.address.landmark}</p>}
              </div>
            </Section>
          )}

          {/* View mode: Designs */}
          {!editing && (
            <Section title="Designs">
              {category?.design_fields?.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {filterSareeSchema(category.design_fields, order, category.slug).map(df => {
                    const sel = (order.style || {})[df.field_key];
                    // Prefer explicit selection value; fall back to matching option's image_url on the category
                    let raw = sel?.value;
                    if (!raw && sel?.key) {
                      const opt = df.options?.find(o => o.key === sel.key);
                      raw = opt?.image_url || opt?.value;
                    }
                    const src = raw
                      ? (String(raw).startsWith('http') ? raw : `${API}${String(raw).startsWith('/') ? '' : '/'}${raw}`)
                      : null;
                    return (
                      <div key={df.field_key} className="border rounded-lg overflow-hidden bg-background">
                        {src ? (
                          <div className="aspect-[3/4] bg-secondary overflow-hidden">
                            <img src={src} alt={sel?.label || df.field_label} className="w-full h-full object-cover object-top" loading="lazy" />
                          </div>
                        ) : (
                          <div className="aspect-[3/4] bg-secondary flex items-center justify-center text-[10px] uppercase tracking-luxe text-muted-foreground">Not selected</div>
                        )}
                        <div className="p-2">
                          <p className="text-[10px] text-muted-foreground uppercase tracking-luxe">{df.field_label}</p>
                          <p className="text-xs font-medium mt-0.5">{sel?.label || sel?.key || <span className="text-muted-foreground">—</span>}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No designs configured.</p>
              )}
            </Section>
          )}

          {/* View mode: Preferences */}
          {!editing && (
            <Section title="Preferences">
              {category?.preferences_schema?.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  {filterSareeSchema(category.preferences_schema, order, category.slug).map(pf => {
                    const v = (order.preferences || {})[pf.key];
                    let display;
                    if (v === undefined || v === null || v === '') display = <span className="text-muted-foreground">Not selected</span>;
                    else if (typeof v === 'string') display = v;
                    else if (Array.isArray(v)) display = v.join(', ');
                    else if (v?.values) display = v.values.join(', ') + (v.comment ? ` — ${v.comment}` : '');
                    else if (v?.value) display = v.value + (v.comment ? ` — ${v.comment}` : '') + (v.custom_text ? ` (${v.custom_text})` : '');
                    else display = JSON.stringify(v);
                    return <div key={pf.key}><span className="text-muted-foreground">{pf.label}:</span> <span className="font-medium capitalize">{display}</span></div>;
                  })}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No preferences configured.</p>
              )}
            </Section>
          )}

          {/* View mode: Measurements */}
          {!editing && order.measurement && (
            <Section title="Measurements">
              <p className="text-xs text-muted-foreground mb-2">Method: {(order.measurement.method || '-').replace(/_/g,' ')}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-xs">
                {(() => {
                  // Some legacy orders store scalar fields nested under `measurement.measurements`; flatten them
                  const src = order.measurement || {};
                  const flat = { ...src, ...(src.measurements && typeof src.measurements === 'object' ? src.measurements : {}) };
                  delete flat.measurements;
                  return Object.entries(flat)
                    .filter(([k]) => !['method','profile_id','profile_name','reference_image','address_id'].includes(k))
                    .filter(([,v]) => v !== '' && v !== null && v !== undefined && typeof v !== 'object')
                    .map(([k,v]) => {
                      const label = category?.measurement_fields?.find(m => m.field_key === k)?.field_label || k.replace(/_/g,' ');
                      return <div key={k}><span className="text-muted-foreground capitalize">{label}:</span> <span className="font-medium">{String(v)} in</span></div>;
                    });
                })()}
              </div>
            </Section>
          )}

          {/* Edit mode (reuses customer customization components) */}
          {editing && category && (
            <div className="border-2 border-primary/20 rounded-xl p-4 space-y-6 bg-primary/[0.01]">
              <div>
                <p className="text-sm font-semibold text-primary">Editing Order</p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Saving here updates only this order. The customer&apos;s measurement profile is created/updated automatically when the order is approved.
                </p>
              </div>

              {category.design_fields?.length > 0 && (
                <div>
                  <div className="uppercase tracking-luxe text-[11px] font-semibold text-gold mb-3">Step 01 of 03 · Style</div>
                  <StyleStep
                    designFields={category.design_fields}
                    selections={editStyle}
                    onChange={setEditStyle}
                    activeSection={editStyleActiveSection}
                    onActiveSectionChange={setEditStyleActiveSection}
                  />
                </div>
              )}

              {category.preferences_schema?.length > 0 && (
                <div>
                  <PreferencesStep
                    preferencesSchema={category.preferences_schema}
                    preferences={editPrefs}
                    onChange={setEditPrefs}
                  />
                </div>
              )}

              {category.measurement_fields?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold mb-2">Measurements (inches)</p>
                  <div className="mb-3">
                    <label className="text-xs text-muted-foreground block mb-1">
                      Profile Name <span className="text-[10px]">(optional — used when saving to customer profile on approval)</span>
                    </label>
                    <Input
                      type="text"
                      value={editMeasurement.profile_name ?? ''}
                      onChange={e => setEditMeasurement(p => ({...p, profile_name: e.target.value}))}
                      placeholder={`e.g. ${order.customer_name || 'Customer'}'s ${order.category_name || 'profile'}`}
                      className="max-w-sm h-8 text-xs"
                      data-testid="edit-meas-profile-name"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {category.measurement_fields.map(mf => (
                      <div key={mf.field_key} className="flex items-center gap-2">
                        <label className="text-xs flex-1">{mf.field_label}{mf.is_required && <span className="text-destructive">*</span>}</label>
                        <Input
                          type={mf.field_type === 'fraction' ? 'text' : 'number'}
                          step="0.25"
                          value={editMeasurement[mf.field_key] ?? ''}
                          onChange={e => setEditMeasurement(p => ({...p, [mf.field_key]: mf.field_type === 'fraction' ? e.target.value : (e.target.value === '' ? '' : parseFloat(e.target.value))}))}
                          placeholder={mf.field_type === 'fraction' ? '12.5/14' : '0'}
                          className="max-w-[100px] h-8 text-xs"
                          data-testid={`edit-meas-${mf.field_key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2 sticky bottom-0 bg-white pb-2 -mx-4 px-4 border-t mt-4">
                <Button size="sm" onClick={saveEdit} disabled={submitting} className="text-xs h-8 gap-1" data-testid="popup-save-edit-btn">
                  <Save className="w-3 h-3" /> {submitting ? 'Saving…' : 'Save Changes'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="text-xs h-8">Cancel</Button>
              </div>
            </div>
          )}

          {/* Status History */}
          {order.status_history?.length > 0 && (
            <Section title="Status History">
              <div className="space-y-1">
                {order.status_history.slice(-8).map((h,i) => (
                  <p key={i} className="text-[10px] text-muted-foreground">
                    {new Date(h.changed_at).toLocaleString()} — <span className="font-medium">{STATUS_CONFIG[h.status]?.label||h.status}</span>
                    {h.notes && ` (${h.notes})`}
                  </p>
                ))}
              </div>
            </Section>
          )}

          {/* Actions */}
          <div className="space-y-3 pt-2 border-t">
            {canEdit && !editing && (
              <Button variant="outline" size="sm" onClick={startEdit} className="text-xs h-8 gap-1" data-testid="popup-edit-btn">
                <Edit2 className="w-3 h-3" /> Edit Order
              </Button>
            )}

            {order.status === 'NEW_ORDER' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(!order.total_amount || order.total_amount === 0) && (
                  <div>
                    <label className="text-xs font-medium block mb-1">Estimated cost (Rs.) <span className="text-destructive">*</span></label>
                    <Input
                      value={estCost}
                      onChange={e => setEstCost(e.target.value)}
                      placeholder="e.g. 2500"
                      className="text-xs h-8"
                      type="number"
                      min="1"
                      data-testid="est-cost-input"
                    />
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium block mb-1">Estimated delivery date <span className="text-destructive">*</span></label>
                  <Input
                    type="date"
                    value={estDelivery}
                    onChange={e => setEstDelivery(e.target.value)}
                    min={new Date().toISOString().slice(0,10)}
                    className="text-xs h-8"
                    data-testid="est-delivery-input"
                  />
                </div>
              </div>
            )}

            <Input
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Notes for this transition..."
              className="text-xs h-8"
              data-testid="popup-notes-input"
            />

            <div className="flex flex-wrap gap-2">
              {allowed.map(s => {
                // Move-back gate: Alteration reopen only for SA + BA
                if (isMoveBack(s) && !canMoveBack) return null;
                if (s === 'APPROVED' && (order.status === 'READY_FOR_DELIVERY' || order.status === 'DELIVERED')) {
                  return (
                    <Button key={s} size="sm" variant="outline" disabled={submitting} onClick={() => handleTransition(s)} className="text-xs h-8 gap-1 text-amber-600 border-amber-400" data-testid={`popup-transition-${s}`}>
                      <AlertCircle className="w-3 h-3" /> Alteration
                    </Button>
                  );
                }
                return (
                  <Button
                    key={s}
                    size="sm"
                    variant={s === 'CANCELLED' || s === 'ON_HOLD' ? 'outline' : 'default'}
                    disabled={submitting}
                    onClick={() => handleTransition(s)}
                    className={`text-xs h-8 gap-1 ${s === 'CANCELLED' ? 'text-destructive border-destructive' : s === 'ON_HOLD' ? 'text-amber-600 border-amber-400' : ''}`}
                    data-testid={`popup-transition-${s}`}
                  >
                    {s === 'CANCELLED' ? <XCircle className="w-3 h-3" /> : s === 'ON_HOLD' ? <Pause className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
                    {STATUS_CONFIG[s]?.label || s}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Delivered popup — comment + amount paid + auto balance */}
      {deliveredOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={() => setDeliveredOpen(false)} data-testid="delivered-popup">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div>
              <h3 className="text-base font-bold">Mark as Delivered</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{order.order_number}</p>
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Amount paid (Rs.)</label>
              <Input
                type="number"
                min="0"
                value={deliveredAmountPaid}
                onChange={e => setDeliveredAmountPaid(e.target.value)}
                placeholder={order.total_amount ? String(order.total_amount) : '0'}
                className="h-9 text-sm"
                data-testid="delivered-amount-paid"
              />
              <div className="grid grid-cols-2 gap-3 mt-2 text-xs">
                <div className="bg-muted/50 rounded-md p-2">
                  <p className="text-muted-foreground text-[10px] uppercase tracking-wider">Total</p>
                  <p className="font-semibold tabular-nums" data-testid="delivered-total">Rs. {order.total_amount || 0}</p>
                </div>
                <div className={`rounded-md p-2 ${balance > 0 ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>
                  <p className="text-[10px] uppercase tracking-wider opacity-80">Balance</p>
                  <p className="font-semibold tabular-nums" data-testid="delivered-balance">Rs. {balance}</p>
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Comment (optional)</label>
              <Input
                value={deliveredComment}
                onChange={e => setDeliveredComment(e.target.value)}
                placeholder="e.g. Handed over at doorstep, verified fit"
                className="h-9 text-sm"
                data-testid="delivered-comment"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => setDeliveredOpen(false)} data-testid="delivered-cancel">Cancel</Button>
              <Button size="sm" disabled={submitting} onClick={submitDelivered} data-testid="delivered-confirm">
                {submitting ? 'Saving…' : 'Confirm Delivery'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Record Payment popup — incremental payments */}
      {paymentOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={() => setPaymentOpen(false)} data-testid="record-payment-popup">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}>
            <div>
              <h3 className="text-base font-bold">Record Payment</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{order.order_number}</p>
            </div>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div className="bg-muted/50 rounded-md p-2 col-span-1">
                <p className="text-muted-foreground text-[10px] uppercase tracking-wider">Total</p>
                <p className="font-semibold tabular-nums">Rs. {order.total_amount || 0}</p>
              </div>
              <div className="bg-green-50 text-green-700 rounded-md p-2 col-span-1">
                <p className="text-[10px] uppercase tracking-wider opacity-80">Already Paid</p>
                <p className="font-semibold tabular-nums">Rs. {order.amount_paid || 0}</p>
              </div>
              <div className="bg-amber-50 text-amber-700 rounded-md p-2 col-span-1">
                <p className="text-[10px] uppercase tracking-wider opacity-80">Current Balance</p>
                <p className="font-semibold tabular-nums">Rs. {Math.max(0, (order.total_amount || 0) - (order.amount_paid || 0))}</p>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">New payment received (Rs.)</label>
              <input
                type="number"
                min="1"
                value={paymentAmount}
                onChange={e => setPaymentAmount(e.target.value)}
                placeholder="e.g. 300"
                className="w-full h-9 text-sm border rounded-md px-3"
                data-testid="payment-amount-input"
                autoFocus
              />
              {paymentAmount && !isNaN(parseFloat(paymentAmount)) && (
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  After this payment: <span className="font-semibold text-foreground tabular-nums">Rs. {Math.min((order.total_amount||0), (order.amount_paid||0) + parseFloat(paymentAmount))}</span> paid,
                  {' '}
                  <span className="font-semibold text-foreground tabular-nums">Rs. {Math.max(0, (order.total_amount||0) - Math.min((order.total_amount||0), (order.amount_paid||0) + parseFloat(paymentAmount)))}</span> balance
                  {((order.amount_paid||0) + parseFloat(paymentAmount)) >= (order.total_amount||0) && <span className="text-green-700 font-semibold"> • Fully paid ✓</span>}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => setPaymentOpen(false)} data-testid="payment-cancel">Cancel</Button>
              <Button size="sm" disabled={submitting} onClick={submitPayment} data-testid="payment-confirm">
                {submitting ? 'Saving…' : 'Record'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <span className="text-muted-foreground text-xs block">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
